import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Ticket, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function Login() {
  const { user, login, selectTenantSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tenantPicker, setTenantPicker] = useState(null); // { selectToken, memberships }

  if (user) return <Navigate to={user.is_platform ? "/admin" : "/dashboard"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password, remember);
    setLoading(false);
    if (res.ok) {
      if (res.needsSelection) {
        setTenantPicker({ selectToken: res.selectToken, memberships: res.memberships });
      } else if (res.user?.is_platform) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } else setError(res.error || "Login failed");
  };

  const onSelectTenant = async (tenantId) => {
    setError("");
    setLoading(true);
    try {
      await selectTenantSession(tenantId, tenantPicker.selectToken, remember);
      navigate("/dashboard");
    } catch (e) {
      setError("Failed to select company. Please try again.");
    }
    setLoading(false);
  };

  const humanizeRole = (role) => {
    if (!role) return "";
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase().replace(/_/g, " ");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      <div className="lg:col-span-3 hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
              <Ticket weight="fill" size={20} />
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Support By ERP4U</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Client Service & Change Requests</div>
            </div>
          </div>
        </div>

        <div className="relative max-w-lg">
          <div className="text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-3">Operations control room</div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
            One platform for every ticket, change request, and SLA you&apos;ll ever ship.
          </h1>
          <p className="mt-5 text-slate-400 text-base leading-relaxed">
            Support By ERP4U unifies your dev team and client conversations — from bug reports to feature voting, work logs to release notes.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-px bg-slate-800 rounded-md overflow-hidden border border-slate-800">
            {[
              { k: "Avg. Resolution", v: "4.2h" },
              { k: "Tickets / mo", v: "1,240" },
              { k: "Client CSAT", v: "4.8 / 5" },
            ].map((s) => (
              <div key={s.k} className="bg-slate-900 px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{s.k}</div>
                <div className="font-display font-semibold text-2xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-[11px] text-slate-500 tracking-wide">
          © {new Date().getFullYear()} Support By ERP4U · A{" "}
          <a href="https://webteampl.com" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white underline">WebTeam</a>{" "}
          product.
        </div>
      </div>

      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {tenantPicker ? (
            <>
              <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-2">Select company</div>
                <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Choose your workspace</h2>
                <p className="text-sm text-slate-500 mt-2">Your account is linked to multiple companies. Select one to continue.</p>
              </div>

              {error && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-4" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="space-y-2" data-testid="tenant-picker">
                {tenantPicker.memberships.map((m) => (
                  <button
                    key={m.tenant_id}
                    onClick={() => onSelectTenant(m.tenant_id)}
                    disabled={loading}
                    className="w-full text-left px-4 py-3 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 group"
                    data-testid={`tenant-option-${m.tenant_id}`}
                  >
                    <div className="font-semibold text-slate-900 text-sm group-hover:text-blue-700">{m.company_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{humanizeRole(m.role)}</div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => { setTenantPicker(null); setError(""); }}
                className="mt-6 text-xs text-slate-400 hover:text-blue-600 w-full text-center"
              >
                ← Back to sign in
              </button>
            </>
          ) : (
          <>
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-2">Access your workspace, tickets & client conversations.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold tracking-wide uppercase text-slate-500">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold tracking-wide uppercase text-slate-500">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
              Remember me for 30 days
            </label>

            {error && (
              <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-submit-button">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={14} weight="bold" className="ml-1.5" /></>)}
            </Button>
          </form>

          <div className="text-xs text-slate-400 text-center mt-6">
            Accounts are created by invitation. Contact your administrator for access.
          </div>
          <div className="text-[11px] text-slate-400 text-center mt-4">
            <a href="https://webteampl.com" target="_blank" rel="noreferrer" className="hover:text-blue-600">webteampl.com</a>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
